/**
 * CHI È IL VERTICE DEL PROPRIO GRUPPO — e perché il tenant di piattaforma non conta.
 *
 * Un'azienda è al vertice quando sopra di lei non c'è **una capogruppo**: o non ha madre, o la
 * madre è il tenant di piattaforma, che non è un'azienda del gruppo ma la radice
 * dell'installazione. Contarlo vorrebbe dire che **nessuna azienda è mai il vertice di sé
 * stessa**, perché tutte gli discendono.
 *
 * ## ⚠️ Perché è un file, e perché arriva tardi
 *
 * Questa regola è stata scritta tre volte in tre giorni, in tre schermate che non si parlano:
 *
 * · la **politica di branding** (`features/admin/brandingPolicy.ts`), dove la catena degli
 *   antenati si ferma sotto la piattaforma — se non si fermasse, una politica scritta lassù
 *   bloccherebbe ogni azienda dell'installazione in un colpo solo;
 * · la **condivisione di gruppo** (`GroupLinkPanel`), che si imposta solo sul vertice;
 * · la **sincronizzazione con un gestionale esterno**, dove «Eredita dalla capogruppo» su una
 *   capogruppo è una scelta che non può fare niente.
 *
 * La terza volta l'ho scritta **sbagliata** — l'ha vista chi la usa, non una prova — ed è il
 * motivo per cui esiste questo file: tre copie di una regola sono tre occasioni di divergere, e
 * la divergenza qui non dà errore, dà una schermata che offre una scelta impossibile.
 *
 * ## ⚠️ Due modi di riconoscere la piattaforma, e servono tutti e due
 *
 * Il `isSystemTenant` della riga quando la riga c'è, e l'id costante quando c'è solo quello —
 * perché il legame verso la madre spesso è **solo un id**: `parentTenantGuid` sulla lettura
 * singola, `relationships.parentTenant` sull'elenco (vedi `parentIdOf` in `brandingPolicy`), e in
 * nessuno dei due casi la riga della madre è a portata di mano.
 */
import { MASTER_TENANT_ID } from '@/features/admin/tenantBranding';

/** La forma minima con cui un'azienda può presentarsi qui: un id, o una riga che ne porta uno. */
export type TenantRef = string | { id?: string | null; isSystemTenant?: boolean | null } | null | undefined;

const idOf = (t: TenantRef): string => (typeof t === 'string' ? t : t?.id ?? '');

/**
 * `true` se quello è il tenant di piattaforma.
 *
 * ⚠️ Il confronto degli id è **senza distinzione di maiuscole**: sul server di sviluppo lo stesso
 * perimetro torna `6B8CCABB-…` accanto a `3892c966-…` a seconda dell'endpoint, e un confronto
 * esatto farebbe fallire il riconoscimento per una lettera. Sull'id della piattaforma — tutte
 * cifre — oggi non cambierebbe niente, ma questa funzione la chiamano anche con altri id.
 */
export function isPlatformTenant(t: TenantRef): boolean {
  if (t && typeof t !== 'string' && t.isSystemTenant === true) return true;
  const id = idOf(t);
  return Boolean(id) && id.toLowerCase() === MASTER_TENANT_ID.toLowerCase();
}

/**
 * `true` se un'azienda con **quella madre** è il vertice del proprio gruppo.
 *
 * Si passa la **madre**, non l'azienda: è il dato che le tre schermate hanno in mano, e chiedere
 * l'azienda costringerebbe ognuna a risolvere la madre a modo suo — che è esattamente il punto
 * in cui le tre copie divergevano.
 *
 * ⚠️ «Nessuna madre» e «madre = piattaforma» sono **lo stesso caso**, e non è un'equivalenza di
 * comodo: un'azienda appesa direttamente alla piattaforma è una capogruppo a tutti gli effetti —
 * sopra di lei non c'è nessuno a cui chiedere, e nessuno che possa decidere al posto suo.
 */
export function isGroupRoot(parent: TenantRef): boolean {
  if (!parent) return true;
  if (typeof parent !== 'string' && !parent.id && parent.isSystemTenant !== true) return true;
  return isPlatformTenant(parent);
}

/* ─────────────────────── l'albero, non i colori ─────────────────────── */

/**
 * LA FORMA MINIMA DI UN'AZIENDA QUANDO SI RAGIONA SULL'ALBERO.
 *
 * ⚠️ Queste tre cose — il tipo, `parentIdOf` e `subsidiariesOf` — stavano in
 * `features/admin/brandingPolicy.ts`, insieme alla politica dei colori. Il 29-09-2026 quella
 * politica è passata al server (`area.branding`) e il file è sparito; **queste no**, perché non
 * parlavano di colori: parlano di chi sta sotto chi. Il posto giusto era sempre stato qui, accanto
 * alla regola del vertice.
 */
export interface TenantNode {
  id: string;
  name?: string | null;
  /** La madre come **relazione**: è l'unica via che l'elenco compila. Vedi `parentIdOf`. */
  parentTenant?: { id?: string | null } | null;
  /** La madre come **attributo**: solo sulla lettura di una singola azienda. */
  parentTenantGuid?: string | null;
  isSystemTenant?: boolean | null;
}

/**
 * L'id della madre, da **tutte e due** le vie con cui il server la dice.
 *
 * ⚠️ E sono due davvero, misurato il 25-09-2026 sul server di sviluppo: su `GET /api/tenants`
 * (l'elenco) l'attributo `parentTenantGuid` torna **vuoto su ogni riga**, e la madre c'è solo
 * dentro `relationships.parentTenant`; su `GET /api/tenants/{id}` è il contrario. Leggendo un
 * campo solo, metà delle chiamate dice «nessuna madre» — che non è un errore, è una risposta, ed
 * è falsa.
 */
export function parentIdOf(t?: TenantNode | null): string | null {
  const fromRelation = t?.parentTenant?.id;
  if (fromRelation) return String(fromRelation);
  return t?.parentTenantGuid ? String(t.parentTenantGuid) : null;
}

/** Le controllate di un'azienda, dirette e indirette, dall'elenco già in pagina. */
export function subsidiariesOf(tenantId: string, all: readonly TenantNode[]): TenantNode[] {
  const me = String(tenantId ?? '').toLowerCase();
  const out: TenantNode[] = [];
  const queue = [me];
  const seen = new Set(queue);
  while (queue.length) {
    const parent = queue.shift()!;
    for (const t of all) {
      const id = String(t.id).toLowerCase();
      if (seen.has(id)) continue;
      if (String(parentIdOf(t) ?? '').toLowerCase() !== parent) continue;
      seen.add(id);
      out.push(t);
      queue.push(id);
    }
  }
  return out;
}
